import React, { useEffect, useState, useCallback, useMemo, useRef, startTransition, memo } from 'react';
import businessInfoService from '../services/businessInfoService';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useRole, useRoleModal } from '../contexts/RoleContext';
import { useNotification, useNotificationState } from '../components/common/NotificationSystem';
import { useWorkerDashboard } from '../contexts/WorkerDashboardContext';
import { buildAppNavItems } from '../utils/navigationForBusinessType';
import { getBusinessTypeId } from '../utils/restaurantMode';
import { useBusinessMeta } from '../features/meta/hooks/useBusinessMeta.js';
import { 
  BarChart3,
  User,
  Pause,
  ChevronDown,
  LogIn,
  LogOut,
  PanelLeft,
  Search,
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import CodeEntryModal from './CodeEntryModal';
import NotificationCenter from './common/NotificationCenter';
import WorkerDashboardModal from './WorkerDashboardModal';
import AppSidebar from './AppSidebar';
import { AdsNotificationBell, BreadcrumbBar, ShellCommandPalette } from './ads';
import AdminShellLayout from './AdminShellLayout';
import '../styles/layout1-professional.css';
import '../styles/pos/pos-dashboard-chrome.css';
import '../styles/manager-desktop-shell.css';
import '../styles/layouts/ads-shell.css';
import '../styles/layouts/shell-enterprise.css';
import {
  buildMainContentClassName,
  buildPageContentClassName,
  resolveRouteLayoutMeta,
} from '../routing/routeLayoutMeta';
import {
  cycleSidebarMode,
  readSidebarMode,
  writeSidebarMode,
} from '../utils/sidebarModePreference';
import {
  readStoredSessionUser,
  persistSessionBusinessLogo
} from '../services/offlineAuthSession';
import { DOMAIN_EVENTS } from '../infrastructure/events/domainEventTypes';
import { isBusinessLogoutInProgress } from '../utils/businessLogoutFlag';
import { WORKER_PAGE_PERMISSION_KEYS } from '../utils/workerPermissions';
import { usePrinterConnectedStatus } from '../hooks/usePrinterConnectedStatus';
import { translateWithVars } from '../utils/i18n/translateWithVars';
import { usePresenceTransition } from '../hooks/usePresenceTransition';
import { scheduleIdleNavPrefetch } from '../utils/navRoutePrefetch';
import {
  MANAGER_IDLE_PREFETCH_PATHS,
  WORKER_IDLE_PREFETCH_PATHS,
} from '../routing/appRouteCatalog';
import {
  runModalSafetyCheck,
  dismissModalsOnRouteChange,
  releaseScrollLockOnRouteChange,
  hasLiveModalPortal,
} from '../utils/modalUtils';

const FIXED_SIDEBAR_MEDIA = '(min-width: 1024px)';

function readFixedSidebarViewport() {
  return (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function' ||
    window.matchMedia(FIXED_SIDEBAR_MEDIA).matches
  );
}

function sidebarModeTitle(mode, tl) {
  if (mode === 'compact') return tl('sidebarCompact');
  if (mode === 'hidden') return tl('sidebarHidden');
  return tl('sidebarExpanded');
}

/**
 * Persistent app shell (Sidebar + Header + page slot).
 * Must NOT subscribe to Inventory/Sales domain slices — only BusinessMeta for nav/type
 * (ADR-0014 §6 / Phase 2.3).
 */
const BusinessShellLayout = ({ children }) => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const mainContentRef = useRef(null);
  const sidebarTriggerRef = useRef(null);
  const { t, isRTL, currentLanguage } = useLanguage();
  const langCode = currentLanguage;
  const tl = useCallback((key) => t(`layoutShell.${key}`), [t]);
  const { 
    currentRole,
    ready: roleReady,
    logoutFromRole, 
    hasPermission 
  } = useRole();
  const {
    setShowCodeModal,
    showCodeModal,
    handleCashRegisterPause,
  } = useRoleModal();
  const { isOpen: showWorkerDashboard, workerId, closeDashboard, openDashboard } =
    useWorkerDashboard();
  const workerDashPresence = usePresenceTransition(Boolean(showWorkerDashboard));
  // Mount code entry when Role asks for it, or when a role is required but missing.
  const codeModalOpen =
    !isBusinessLogoutInProgress() &&
    (Boolean(showCodeModal) || (Boolean(roleReady) && !currentRole));
  const codeModalPresence = usePresenceTransition(codeModalOpen);
  const { getUnreadCount } = useNotification();
  const { persistentNotifications } = useNotificationState();
  const { currentBusinessType, currentBusinessId } = useBusinessMeta();
  const isWorkerSession = currentRole?.type === 'worker';
  const printerStatus = usePrinterConnectedStatus(isWorkerSession);
  // persistentNotifications identity changes when inbox updates — intentional recompute.
  const unreadCount = useMemo(
    () => getUnreadCount(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inbox list is the change signal
    [getUnreadCount, persistentNotifications]
  );

  const businessTypeId = getBusinessTypeId(currentBusinessType);
  const layoutMeta = useMemo(
    () =>
      resolveRouteLayoutMeta(location.pathname, currentRole?.type, {
        businessTypeId,
      }),
    [location.pathname, currentRole?.type, businessTypeId]
  );
  const isRestaurantPosShell =
    Boolean(layoutMeta.fitPos) && businessTypeId === 'restaurant';
  const mainContentClassName = buildMainContentClassName(layoutMeta);
  const pageContentClassName = buildPageContentClassName(layoutMeta);

  // State management
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [notificationPosition, setNotificationPosition] = useState('header');
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);
  const [sidebarMode, setSidebarMode] = useState(() => readSidebarMode());
  const [fixedSidebarViewport, setFixedSidebarViewport] = useState(readFixedSidebarViewport);
  const [overlaySidebarOpen, setOverlaySidebarOpen] = useState(false);
  const [sidebarNavClose, setSidebarNavClose] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const notifCenterPresence = usePresenceTransition(showNotificationCenter);

  // Keep-alive pages stay mounted — dismiss portaled modals on nav so they
  // don't float over the next route and jam the scroll lock. Also reset scroll
  // after the shell class swap paints (fit-manager → document-scroll).
  useEffect(() => {
    const hasModal = hasLiveModalPortal();
    if (hasModal) {
      dismissModalsOnRouteChange(location.pathname);
    }
    setCommandPaletteOpen(false);
    setShowNotificationCenter(false);
    setOverlaySidebarOpen(false);
    releaseScrollLockOnRouteChange();
    const main = mainContentRef.current;
    let raf = 0;
    const resetScroll = () => {
      if (main) {
        main.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    };
    raf = window.requestAnimationFrame(resetScroll);
    const timer = setTimeout(() => {
      releaseScrollLockOnRouteChange();
      runModalSafetyCheck();
    }, hasModal ? 160 : 0);
    return () => {
      window.cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const media = window.matchMedia(FIXED_SIDEBAR_MEDIA);
    const handleChange = (event) => {
      setFixedSidebarViewport(event.matches);
      setOverlaySidebarOpen(false);
    };

    setFixedSidebarViewport(media.matches);
    media.addEventListener?.('change', handleChange);
    return () => media.removeEventListener?.('change', handleChange);
  }, []);

  useEffect(() => {
    if (fixedSidebarViewport || !overlaySidebarOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOverlaySidebarOpen(false);
        requestAnimationFrame(() => sidebarTriggerRef.current?.focus());
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fixedSidebarViewport, overlaySidebarOpen]);

  const toggleCommandPalette = useCallback(() => {
    setOverlaySidebarOpen(false);
    setCommandPaletteOpen((open) => !open);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggleCommandPalette();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleCommandPalette]);

  useEffect(() => {
    if (fixedSidebarViewport) return undefined;
    // Snap overlay shut on route/breakpoint change — avoid 220ms slide racing page paint
    setSidebarNavClose(true);
    setOverlaySidebarOpen(false);
    const frame = window.requestAnimationFrame(() => {
      setSidebarNavClose(false);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [fixedSidebarViewport, location.pathname]);

  useEffect(() => {
    const mainContent = mainContentRef.current;
    if (!mainContent) return undefined;

    const shouldTrapFocus = !fixedSidebarViewport && overlaySidebarOpen;
    mainContent.inert = shouldTrapFocus;

    let frameId;
    if (shouldTrapFocus) {
      frameId = requestAnimationFrame(() => {
        document.querySelector('#app-sidebar .app-sidebar-toggle-btn')?.focus();
      });
    }

    return () => {
      mainContent.inert = false;
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [fixedSidebarViewport, overlaySidebarOpen]);
  
  // Get current business from KV and load logo from businessInfoService
  const [currentBusiness, setCurrentBusiness] = useState(null);
  
  const loadBusiness = useCallback(async () => {
    try {
      const user = readStoredSessionUser();
      if (!user) {
        setCurrentBusiness(null);
        return;
      }

      const business = user?.business || null;
        
      if (business) {
        try {
          const businessInfo = await businessInfoService.getBusinessInfo(business.id);
          if (businessInfo?.logo) {
            business.logo = businessInfo.logo;
            if (!isBusinessLogoutInProgress()) {
              persistSessionBusinessLogo(businessInfo.logo);
            }
          }
        } catch (error) {
          console.warn('Error loading logo from businessInfoService:', error);
        }
          
        setCurrentBusiness(business);
      } else {
        setCurrentBusiness(null);
      }
    } catch (error) {
      console.error('Error getting current business:', error);
      setCurrentBusiness(null);
    }
  }, []);

  useEffect(() => {
    loadBusiness();
    
    // Listen for logo updates
    const handleLogoUpdate = (event) => {
      if (event.detail && event.detail.logo) {
        setCurrentBusiness(prev => {
          if (prev) {
            return { ...prev, logo: event.detail.logo };
          }
          return prev;
        });
      } else {
        // Reload business data if logo was deleted
        loadBusiness();
      }
    };
    
    const onDomainLogo = (event) => {
      if (event?.detail?.type === DOMAIN_EVENTS.BUSINESS_LOGO_UPDATED) {
        handleLogoUpdate({ detail: event.detail.payload || {} });
      }
    };
    
    window.addEventListener('elkheir:domain-event', onDomainLogo);
    
    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainLogo);
    };
  }, [loadBusiness]);

  // Handle notification center from Dashboard fullscreen mode.
  // Read pathname from a ref so this listener is not re-bound every hop.
  useEffect(() => {
    const handleOpenNotificationCenter = () => {
      setNotificationPosition(pathnameRef.current === '/' ? 'fullscreen' : 'header');
      startTransition(() => {
        setShowNotificationCenter(true);
      });
    };

    window.addEventListener('openNotificationCenter', handleOpenNotificationCenter);
    return () => window.removeEventListener('openNotificationCenter', handleOpenNotificationCenter);
  }, []);

  // Warm common lazy routes once the shell is idle (does not block first paint).
  useEffect(() => {
    if (!currentRole) return undefined;
    // Managers hop across many registries — warm the usual destinations early.
    // Workers: only lazy pages (inventory/stock are already eager in ConditionalRoutes).
    const cancelPrefetch = scheduleIdleNavPrefetch(
      currentRole.type === 'worker' ? WORKER_IDLE_PREFETCH_PATHS : MANAGER_IDLE_PREFETCH_PATHS
    );
    // Workers often open session reports — warm that chunk off the critical path.
    let cancelWorkerDash;
    if (currentRole.type === 'worker' && typeof window !== 'undefined') {
      const warm = () => {
        void import('./WorkerDashboardModal');
      };
      if (typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(warm, { timeout: 8000 });
        cancelWorkerDash = () => window.cancelIdleCallback?.(id);
      } else {
        const timerId = window.setTimeout(warm, 5000);
        cancelWorkerDash = () => window.clearTimeout(timerId);
      }
    }
    return () => {
      cancelPrefetch?.();
      cancelWorkerDash?.();
    };
  }, [currentRole?.type, currentRole]);

  // Close manager dropdown when clicking outside
  useEffect(() => {
    if (!showManagerDropdown) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.manager-dropdown')) {
        setShowManagerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showManagerDropdown]);

  // Menu items configuration
  const menuItems = useMemo(() => {
    const items = buildAppNavItems({
      t,
      currentLanguage: langCode,
      businessType: currentBusinessType,
      hasPermission
    });
    if (currentRole?.type !== 'worker') return items;
    return items.map((item) =>
      item.path === '/' ? { ...item, label: tl('workerDashboardTitle') } : item
    );
  }, [t, tl, langCode, currentBusinessType, hasPermission, currentRole?.type]);

  // Get current page title
  const currentPageTitle = useMemo(() => {
    const fromNav = menuItems.find((item) => item.path === location.pathname)?.label;
    if (fromNav) return fromNav;
    if (location.pathname === '/payment-report') {
      return t('managerDashboard.paymentReport') || t('dashboard');
    }
    return t('dashboard');
  }, [menuItems, location.pathname, t]);

  const breadcrumbItems = useMemo(() => {
    if (!currentPageTitle || location.pathname === '/') return [];
    return [{ label: currentPageTitle, path: location.pathname }];
  }, [currentPageTitle, location.pathname]);

  const searchShortcutLabel = useMemo(() => {
    if (typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)) {
      return '⌘K';
    }
    return tl('searchShortcut');
  }, [tl]);

  // Get role display name
  const roleDisplayName = useMemo(() => {
    if (!currentRole) return tl('login');
    if (currentRole.type === 'manager') return tl('manager');
    return currentRole.name;
  }, [currentRole, tl]);

  const managerOwnerName = useMemo(() => {
    const user = readStoredSessionUser();
    const business = user?.business;
    return (
      business?.owner_name
      || user?.user?.user_metadata?.owner_name
      || ''
    );
  }, [currentRole, currentBusinessId]);

  // Handlers
  const handleNotificationClick = useCallback(() => {
    setNotificationPosition('header');
    startTransition(() => {
      setShowNotificationCenter((prev) => !prev);
    });
  }, []);

  const handleNotificationCenterClose = useCallback(() => {
    startTransition(() => {
      setShowNotificationCenter(false);
    });
  }, []);

  const handleManagerDropdownToggle = useCallback(() => {
    setShowManagerDropdown(prev => !prev);
  }, []);

  const handleWorkerReportClick = useCallback(() => {
    if (currentRole?.id) {
      openDashboard(currentRole.id);
    }
    setShowManagerDropdown(false);
  }, [currentRole?.id, openDashboard]);

  const handleRoleLoginClick = useCallback(() => {
    startTransition(() => {
      setShowCodeModal(true);
    });
    setShowManagerDropdown(false);
  }, [setShowCodeModal]);

  const handleCashPauseClick = useCallback(() => {
    handleCashRegisterPause();
    setShowManagerDropdown(false);
  }, [handleCashRegisterPause]);

  const handleRoleLogoutClick = useCallback(() => {
    logoutFromRole();
    setShowManagerDropdown(false);
  }, [logoutFromRole]);

  /** Always show the tenant brand in the shell header (desktop sidebar and mobile). */
  const businessDisplayName = useMemo(() => {
    const raw = currentBusiness?.business_name || currentBusiness?.name;
    const name = String(raw || '').trim();
    return name || 'AMAN';
  }, [currentBusiness?.business_name, currentBusiness?.name]);

  const roleDropdown = useMemo(
    () => (
      <div className="manager-dropdown">
        <button
          type="button"
          className={`manager-button ${currentRole ? `manager-button-${currentRole.type}` : 'manager-button-login'}`}
          onClick={handleManagerDropdownToggle}
          title={currentRole ? tl('userOptions') : tl('connectionLogin')}
          aria-expanded={showManagerDropdown}
          aria-haspopup="true"
        >
          <User size={16} />
          {currentRole?.type === 'manager' ? (
            <span className="manager-button-label">
              {managerOwnerName || tl('manager')}
              {managerOwnerName ? (
                <span className="manager-button-abbr"> · {tl('managerAbbr')}</span>
              ) : null}
            </span>
          ) : (
            <span>{roleDisplayName}</span>
          )}
          <ChevronDown
            size={14}
            className={`dropdown-chevron ${showManagerDropdown ? 'open' : ''}`}
          />
        </button>

        {showManagerDropdown && (
          <div className="manager-dropdown-menu" role="menu">
            <div className="manager-dropdown-tenant shell-user-tenant" role="presentation">
              <span className="shell-user-tenant__label">{businessDisplayName}</span>
            </div>
            {!currentRole && (
              <button
                type="button"
                className="dropdown-menu-item login-item"
                onClick={handleRoleLoginClick}
                role="menuitem"
              >
                <LogIn size={16} />
                <span>{tl('roleLogin')}</span>
              </button>
            )}

            {currentRole?.type === 'worker' && (
              <button
                type="button"
                className="dropdown-menu-item reports-item"
                onClick={handleWorkerReportClick}
                role="menuitem"
                title={tl('myReports')}
              >
                <BarChart3 size={16} />
                <span>{tl('myReports')}</span>
              </button>
            )}

            {currentRole?.type === 'worker' && (
              <button
                type="button"
                className="dropdown-menu-item pause-item"
                onClick={handleCashPauseClick}
                role="menuitem"
              >
                <Pause size={16} />
                <span>{tl('pause')}</span>
              </button>
            )}

            <button
              type="button"
              className="dropdown-menu-item logout-item"
              onClick={handleRoleLogoutClick}
              role="menuitem"
              title={tl('logoutHint')}
            >
              <LogOut size={16} />
              <span>{tl('logout')}</span>
            </button>
          </div>
        )}
      </div>
    ),
    [
      businessDisplayName,
      currentRole,
      handleCashPauseClick,
      handleManagerDropdownToggle,
      handleRoleLoginClick,
      handleRoleLogoutClick,
      handleWorkerReportClick,
      managerOwnerName,
      roleDisplayName,
      showManagerDropdown,
      tl,
    ]
  );

  const useDesktopSidebar = useMemo(() => {
    if (!currentRole) return false;
    return WORKER_PAGE_PERMISSION_KEYS.some((key) => hasPermission(key));
  }, [currentRole, hasPermission]);

  const closeOverlaySidebar = useCallback((restoreFocus = false) => {
    setOverlaySidebarOpen(false);
    if (restoreFocus) {
      requestAnimationFrame(() => sidebarTriggerRef.current?.focus());
    }
  }, []);

  // Fit surfaces must never sit under the tablet overlay scrim (z-index blocks clicks).
  // Do not force-unlock while a live portal (cash/code/scanner) is still open.
  useEffect(() => {
    if (!layoutMeta.fitPos && !layoutMeta.fitManager) return undefined;
    setSidebarNavClose(true);
    setOverlaySidebarOpen(false);
    if (!hasLiveModalPortal()) {
      dismissModalsOnRouteChange(location.pathname);
      releaseScrollLockOnRouteChange();
    }
    const frame = window.requestAnimationFrame(() => setSidebarNavClose(false));
    const timer = setTimeout(releaseScrollLockOnRouteChange, 160);
    return () => {
      window.cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [layoutMeta.fitPos, layoutMeta.fitManager, location.pathname]);

  const toggleDesktopSidebar = useCallback(() => {
    if (!fixedSidebarViewport) {
      if (overlaySidebarOpen) {
        closeOverlaySidebar(true);
      } else {
        setOverlaySidebarOpen(true);
      }
      return;
    }

    setSidebarMode((prev) => {
      const next = cycleSidebarMode(prev);
      writeSidebarMode(next);
      return next;
    });
  }, [closeOverlaySidebar, fixedSidebarViewport, overlaySidebarOpen]);

  const restoreSidebarFromHidden = useCallback(() => {
    setSidebarMode(() => {
      writeSidebarMode('expanded');
      return 'expanded';
    });
  }, []);

  const openSidebarFromHeader = useCallback(() => {
    if (fixedSidebarViewport) {
      restoreSidebarFromHidden();
      return;
    }
    toggleDesktopSidebar();
  }, [fixedSidebarViewport, restoreSidebarFromHidden, toggleDesktopSidebar]);

  useEffect(() => {
    const onOpenSidebar = () => openSidebarFromHeader();
    window.addEventListener('aman:shell-open-sidebar', onOpenSidebar);
    return () => window.removeEventListener('aman:shell-open-sidebar', onOpenSidebar);
  }, [openSidebarFromHeader]);

  // Restaurant premium POS: start with sidebar hidden so the caisse stays fullscreen until opened.
  useEffect(() => {
    if (!isRestaurantPosShell) return;
    if (location.pathname !== '/') return;
    setSidebarMode((prev) => {
      if (prev === 'hidden') return prev;
      writeSidebarMode('hidden');
      return 'hidden';
    });
    setOverlaySidebarOpen(false);
  }, [isRestaurantPosShell, location.pathname]);

  const sidebarVisible = fixedSidebarViewport
    ? sidebarMode !== 'hidden'
    : overlaySidebarOpen;
  const sidebarCompact = fixedSidebarViewport && sidebarMode === 'compact';
  const showSidebarOpenInHeader =
    useDesktopSidebar &&
    (fixedSidebarViewport ? sidebarMode === 'hidden' : !overlaySidebarOpen);

  // Kitchen monitor: bare shell — no sidebar / header / command palette (2nd screen).
  // Phase T: cuisine stays light-locked (constitution) even when product theme is dark.
  if (layoutMeta.kioskShell) {
    return (
      <div
        className="layout ads-shell layout--kiosk-shell"
        data-theme="light"
        data-ads-theme-lock="light"
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <div className={mainContentClassName} ref={mainContentRef}>
          <main className={pageContentClassName}>
            <div className="page-view page-view--kiosk">{children}</div>
          </main>
        </div>
      </div>
    );
  }

  // POS / restaurant sell shell: keep light token slot under product dark (Phase T opt-out).
  const posLightThemeLock = Boolean(layoutMeta.fitPos || isRestaurantPosShell);

  return (
    <div
      className={`layout ads-shell${useDesktopSidebar ? ' layout--with-sidebar' : ''}${
        useDesktopSidebar && fixedSidebarViewport && sidebarMode === 'hidden'
          ? ' layout--sidebar-hidden'
          : ''
      }${
        useDesktopSidebar && fixedSidebarViewport && sidebarMode === 'compact'
          ? ' layout--sidebar-compact'
          : ''
      }${
        useDesktopSidebar && !fixedSidebarViewport && !layoutMeta.fitPos
          ? ` layout--sidebar-overlay${overlaySidebarOpen ? ' layout--sidebar-open' : ''}${
              sidebarNavClose ? ' layout--sidebar-nav-close' : ''
            }`
          : ''
      }${layoutMeta.fitPos ? ' layout--fit-pos' : ''}${
        isRestaurantPosShell ? ' layout--restaurant-pos' : ''
      }${layoutMeta.fitManager ? ' layout--fit-manager' : ''}`}
      {...(posLightThemeLock
        ? { 'data-theme': 'light', 'data-ads-theme-lock': 'light' }
        : {})}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      {/* Notification animations live in layout1-professional.css */}

      {useDesktopSidebar && (
        <AppSidebar
          hidden={!sidebarVisible}
          sidebarExpanded={sidebarVisible}
          compact={sidebarCompact}
          onToggleSidebar={toggleDesktopSidebar}
          toggleTitle={sidebarModeTitle(sidebarMode, tl)}
          navItems={menuItems}
        />
      )}
      {useDesktopSidebar &&
      !fixedSidebarViewport &&
      overlaySidebarOpen &&
      !layoutMeta.fitPos &&
      !layoutMeta.fitManager ? (
        <button
          type="button"
          className="app-sidebar-scrim"
          onClick={() => closeOverlaySidebar(true)}
          aria-label={tl('hideMenu')}
          tabIndex={-1}
        />
      ) : null}

      <div className={mainContentClassName} ref={mainContentRef}>
        {/* Restaurant POS owns its chrome — never mount AppHeader (avoids reload flash). */}
        {!isRestaurantPosShell ? (
        <header
          className={`main-header${
            location.pathname === '/' && currentRole?.type === 'worker'
              ? ' main-header--worker-pos main-header--sell-mode'
              : ''
          }${layoutMeta.fitManager ? ' main-header--dashboard-home' : ''}${
            layoutMeta.contextToolbarOwnsTitle && currentRole?.type === 'manager'
              ? ' main-header--context-owned'
              : ''
          }${showManagerDropdown ? ' main-header--menu-open' : ''}`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Left: Business Info */}
          <div className="header-left">
            {showSidebarOpenInHeader && (
              <button
                type="button"
                className="manager-sidebar-header-btn shell-sidebar-open-btn"
                ref={sidebarTriggerRef}
                onClick={openSidebarFromHeader}
                aria-expanded={sidebarVisible}
                aria-controls="app-sidebar"
                title={tl('showMenu')}
                aria-label={tl('showMenu')}
              >
                <PanelLeft size={20} strokeWidth={1.35} aria-hidden />
              </button>
            )}
            <div className="business-header-info">
              <h1 className="business-name" title={businessDisplayName}>
                {businessDisplayName}
              </h1>
            </div>
            {layoutMeta.fitPos && isWorkerSession && printerStatus.connected ? (
              <div className="header-clock-wrap">
                <span
                  className={`header-printer-dot${printerStatus.partial ? ' header-printer-dot--partial' : ''}`}
                  role="status"
                  aria-live="polite"
                  title={
                    printerStatus.summaryText
                      ? translateWithVars(tl('printerRolesDetail'), {
                          detail: printerStatus.summaryText
                        })
                      : printerStatus.printerName
                        ? translateWithVars(tl('printerConnectedNamed'), {
                            name: printerStatus.printerName
                          })
                        : tl('printerConnected')
                  }
                  aria-label={
                    printerStatus.partial
                      ? tl('printerPartial')
                      : printerStatus.printerName
                        ? translateWithVars(tl('printerConnectedNamed'), {
                            name: printerStatus.printerName
                          })
                        : tl('printerConnected')
                  }
                />
              </div>
            ) : null}
          </div>

          {/* Center: page context — home dashboard title lives here; sub-pages use ContextToolbar */}
          <div className="header-center">
            {location.pathname === '/' && currentRole?.type === 'worker' ? null : layoutMeta.fitManager &&
            currentRole?.type === 'manager' ? (
              <div className="shell-page-context">
                <h2 className="page-title shell-page-context__title">{currentPageTitle}</h2>
              </div>
            ) : layoutMeta.contextToolbarOwnsTitle && currentRole?.type === 'manager' ? null : breadcrumbItems.length > 0 ? (
              <BreadcrumbBar
                items={breadcrumbItems}
                homeLabel={tl('breadcrumbHome')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            ) : (
              <h2 className="page-title">{currentPageTitle}</h2>
            )}
          </div>

          {/* Right: Actions */}
          <div className="header-right">
            {useDesktopSidebar &&
            menuItems.length > 0 &&
            !(location.pathname === '/' && currentRole?.type === 'worker') ? (
              <button
                type="button"
                className="shell-search-trigger"
                onClick={toggleCommandPalette}
                title={tl('searchPages')}
                aria-keyshortcuts={searchShortcutLabel}
              >
                <Search size={16} strokeWidth={1.75} aria-hidden />
                <span className="shell-search-trigger__label">{tl('searchPages')}</span>
                <kbd className="shell-search-trigger__hint">{searchShortcutLabel}</kbd>
              </button>
            ) : null}

            {!useDesktopSidebar && <LanguageSwitcher />}

            {currentRole?.type === 'worker' && roleDropdown}

            {!(location.pathname === '/' && currentRole?.type === 'worker') && (
              <AdsNotificationBell
                unreadCount={unreadCount}
                onClick={handleNotificationClick}
                title={tl('notifications')}
                aria-label={tl('notifications')}
              />
            )}

            {currentRole?.type !== 'worker' && roleDropdown}
          </div>
        </header>
        ) : null}

        <main className={pageContentClassName}>
          <div className="page-view">{children}</div>
        </main>
      </div>
      
      {/* Shell overlays: presence-gated, eager imports (no Suspense/lazy here). */}
      {codeModalPresence.present ? <CodeEntryModal /> : null}
      {notifCenterPresence.present ? (
        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={handleNotificationCenterClose}
          position={notificationPosition}
        />
      ) : null}
      {workerDashPresence.present ? (
        <WorkerDashboardModal
          isOpen={showWorkerDashboard}
          onClose={closeDashboard}
          workerId={workerId}
        />
      ) : null}

      <ShellCommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        navItems={menuItems}
        placeholder={tl('searchPages')}
        emptyLabel={tl('searchNoResults')}
        shortcutHint={searchShortcutLabel}
        dir={isRTL ? 'rtl' : 'ltr'}
      />
    </div>
  );
};

const Layout = ({ children, variant = 'business', onAdminLogout }) => {
  if (variant === 'admin') {
    return <AdminShellLayout onLogout={onAdminLogout}>{children}</AdminShellLayout>;
  }
  return <BusinessShellLayout>{children}</BusinessShellLayout>;
};

export default memo(Layout);